import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-layout>` says about itself.
 *
 * ── A REGION IS NOT A LAYOUT ─────────────────────────────────────────────────
 * A page template's regions decide where a SECTION sits; this decides how the pieces sit INSIDE one.
 * The incumbent lets both do the same job and its own authors cannot tell which to reach for, so the
 * boundary is stated here, where somebody placing the piece will read it (CAOS-590).
 *
 * ── WHAT AN EXAMPLE CANNOT SHOW ──────────────────────────────────────────────
 * An item's per-breakpoint size answers to the width of the WINDOW, so narrowing the stage does not
 * move a column onto its own line. The examples show each arrangement at the width the page is read
 * at; the responsive behaviour is described under `placing` and on LayoutItem.
 */
const docs: ComponentDocs = {
  aliases: ['grid', 'columns', 'row', 'flex layout', 'responsive grid', 'two columns', 'side by side', 'container'],

  answers: [
    'put two things side by side inside a section',
    'make columns that stack on a phone',
    'split a section into a wide column and a narrow one',
    'line pieces up along a row, spaced or centred',
  ],

  placements: ['recordPage', 'homePage', 'appPage'],

  inputs: [
    {
      name: 'horizontalAlign',
      attribute: 'horizontal-align',
      type: 'text',
      options: ['start', 'center', 'end', 'space', 'spread'],
      description:
        'How the items share what is left of the row: packed at the start, centred, packed at the end, ' +
        'with equal space around each one, or spread so the first and last reach the edges.',
      default: 'start',
    },
    {
      name: 'verticalAlign',
      attribute: 'vertical-align',
      type: 'text',
      options: ['start', 'center', 'end', 'stretch'],
      description:
        'Where items of different heights sit across the row — top, middle or bottom — or stretched to the ' +
        'tallest one on their line.',
      default: 'start',
    },
  ],

  slots: [
    {
      name: '',
      takes:
        'LayoutItems, one per column. A LayoutItem can hold another Layout, which is how a column is split ' +
        'into two stacked halves.',
    },
  ],

  parts: [{ part: 'layout', is: 'The wrapping row the items are laid out along.' }],

  placing:
    'Place it inside a section, then place a LayoutItem inside it for each column and the pieces inside ' +
    'those. Items that do not fit on the row wrap onto the next line, so two items that each ask for the ' +
    'full width below the medium breakpoint and half above it sit side by side on a wide screen and stack ' +
    'on a phone. The layout sets no stacking layer and clips nothing, so a menu or picker inside a column ' +
    'still opens over the page.',

  whenToUse:
    'When a section holds more than one thing and they belong next to each other on a wide screen — a ' +
    'form beside its summary, a chart beside the figures it draws, two halves of a record.',

  insteadUse: [
    {
      name: 'CardGrid',
      when: 'The things side by side are records from one list, all the same shape, and the count is not fixed.',
    },
    {
      name: 'ActionBar',
      when: 'What sits in a row is a set of actions — buttons with one primary among them.',
    },
    {
      name: 'DetailSection',
      when: 'What needs arranging is a record’s fields, which a detail section already sets out in columns.',
    },
  ],

  accessibility:
    'A layout adds no role and no landmark: it is arrangement, not structure, and a screen reader hears the ' +
    'pieces it holds rather than the grid. Reading order and tab order follow the order the items are ' +
    'placed in, never where they are drawn. A size changes only width, never order, so what a person ' +
    'hears on a wide screen is the same sequence somebody sees stacked on a phone. Place items in the order ' +
    'they should be read.',

  examples: [
    {
      key: 'two-columns',
      label: 'Two columns',
      summary: 'The plainest layout: two items, each half the row, standing side by side.',
      placement: {
        component: 'caos-layout',
        children: [
          { component: 'caos-layout-item', inputs: { size: 6 }, children: [{ component: 'caos-tag', children: ['First column'] }] },
          { component: 'caos-layout-item', inputs: { size: 6 }, children: [{ component: 'caos-tag', children: ['Second column'] }] },
        ],
      },
    },
    {
      key: 'nested',
      label: 'A column split in two',
      summary: 'An item holding a layout of its own — one wide column beside a narrow one that stacks two halves.',
      placement: {
        component: 'caos-layout',
        children: [
          { component: 'caos-layout-item', inputs: { size: 8 }, children: [{ component: 'caos-tag', children: ['Main column'] }] },
          {
            component: 'caos-layout-item',
            inputs: { size: 4 },
            children: [
              {
                component: 'caos-layout',
                children: [
                  { component: 'caos-layout-item', inputs: { size: 12 }, children: [{ component: 'caos-tag', children: ['Upper half'] }] },
                  { component: 'caos-layout-item', inputs: { size: 12 }, children: [{ component: 'caos-tag', children: ['Lower half'] }] },
                ],
              },
            ],
          },
        ],
      },
    },
    {
      key: 'space-around',
      label: 'Spaced around',
      summary: 'Items narrower than the row with equal room left before, between and after each of them.',
      placement: {
        component: 'caos-layout',
        inputs: { horizontalAlign: 'space' },
        children: [
          { component: 'caos-layout-item', inputs: { size: 3 }, children: [{ component: 'caos-tag', children: ['One'] }] },
          { component: 'caos-layout-item', inputs: { size: 3 }, children: [{ component: 'caos-tag', children: ['Two'] }] },
          { component: 'caos-layout-item', inputs: { size: 3 }, children: [{ component: 'caos-tag', children: ['Three'] }] },
        ],
      },
    },
    {
      key: 'spread',
      label: 'Spread to the edges',
      summary: 'The same three items pushed apart until the first and last reach the ends of the row.',
      placement: {
        component: 'caos-layout',
        inputs: { horizontalAlign: 'spread' },
        children: [
          { component: 'caos-layout-item', inputs: { size: 3 }, children: [{ component: 'caos-tag', children: ['One'] }] },
          { component: 'caos-layout-item', inputs: { size: 3 }, children: [{ component: 'caos-tag', children: ['Two'] }] },
          { component: 'caos-layout-item', inputs: { size: 3 }, children: [{ component: 'caos-tag', children: ['Three'] }] },
        ],
      },
    },
    {
      key: 'vertical-center',
      label: 'Centred across the row',
      summary: 'Items of different heights lined up through their middles instead of along their tops.',
      placement: {
        component: 'caos-layout',
        inputs: { verticalAlign: 'center' },
        children: [
          {
            component: 'caos-layout-item',
            inputs: { size: 6, padding: 'around-large' },
            children: [{ component: 'caos-tag', children: ['Taller item'] }],
          },
          { component: 'caos-layout-item', inputs: { size: 6 }, children: [{ component: 'caos-tag', children: ['Shorter item'] }] },
        ],
      },
    },
    {
      key: 'vertical-stretch',
      label: 'Stretched to the tallest',
      summary: 'Every item on the line made as tall as the tallest one, so columns with edges line up.',
      placement: {
        component: 'caos-layout',
        inputs: { verticalAlign: 'stretch' },
        children: [
          {
            component: 'caos-layout-item',
            inputs: { size: 6, padding: 'around-large' },
            children: [{ component: 'caos-tag', children: ['Taller item'] }],
          },
          { component: 'caos-layout-item', inputs: { size: 6 }, children: [{ component: 'caos-tag', children: ['Stretched item'] }] },
        ],
      },
    },
  ],
};

export default docs;
